import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

// material UI
import List from '@material-ui/core/List';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';

import ReminderListItem from '../notifications/ReminderListItem';
import CandidateReminderListItem from '../notifications/CandidateReminderListItem';
import UpdateCounterPartReminderPopUpContainer from '../notifications/updateCounterPartReminderPopUpContainer';

const createEmptyCounterPartReminder = () => ({
  id: '',
  companyId: '',
  counterPartId: '',
  note: '',
  triggerDate: new Date(),
  notificationId: ''
});

const ReminderList = ({
  reminders,
  variant,
  timeSeparation,
  handleSnackbarOpen,
  onIsReadButtonClick,
  onDeleteButtonClick,
  onEditButtonClick,
  onRowClick,
  errorHandling
}) => {
  const [updateCounterPartReminderPopUpIsOpen, setUpdateCounterPartReminderPopUpIsOpen] = useState(false);
  const [counterPartReminderToEdit, setCounterPartReminderToEdit] = useState(createEmptyCounterPartReminder);

  const handleEditButtonClick = useCallback(
    counterPartReminder => {
      setCounterPartReminderToEdit(counterPartReminder);
      setUpdateCounterPartReminderPopUpIsOpen(true);
      onEditButtonClick();
    },
    [onEditButtonClick]
  );

  const renderReminderItems = (dateSpanFrom, dateSpanTill, withTimeSeparation = true) => {
    const array = [];
    let isFirstItem = true;
    reminders.forEach(reminder => {
      let skipTheDate = false;

      if (withTimeSeparation) {
        let timeTriggerDate = new Date(); // hack?
        if (reminder.dueDate) {
          timeTriggerDate = new Date(reminder.dueDate).getTime();
        } else if (reminder.triggerDate) {
          timeTriggerDate = new Date(reminder.triggerDate).getTime();
        }

        const timespanFrom = new Date().getTime() - dateSpanFrom * 24 * 60 * 60 * 1000;
        const timespanTill = new Date().getTime() - dateSpanTill * 24 * 60 * 60 * 1000;
        if (!(timeTriggerDate <= timespanFrom && timeTriggerDate > timespanTill)) {
          skipTheDate = true;
        }
      }
      if (!skipTheDate) {
        if (!isFirstItem) {
          array.push(<Divider key={`dividerFor${reminder.id}`} />);
        }
        isFirstItem = false;
        if (variant === 'candidate') {
          const candidateName = reminder.candidate
            ? `${reminder.candidate.firstName || ''} ${reminder.candidate.lastName || ''}`.trim()
            : '';
          array.push(
            <CandidateReminderListItem
              key={reminder.id}
              dueDate={reminder.dueDate}
              title="Wiedervorlage"
              description={reminder.note}
              candidateName={candidateName}
              isRead={reminder.isDone}
              id={reminder.id}
              candidateId={(reminder.candidate || {}).id}
              onIsReadButtonClick={onIsReadButtonClick}
              onDeleteButtonClick={onDeleteButtonClick}
              errorHandling={errorHandling}
            />
          );
        } else {
          array.push(
            <ReminderListItem
              key={reminder.id}
              dueDate={reminder.dueDate}
              title={`Wiedervorlage ${reminder.company.name}`}
              description={reminder.note}
              counterPart={reminder.counterPart}
              isRead={reminder.isDone}
              id={reminder.id}
              companyId={reminder.company.id}
              onIsReadButtonClick={onIsReadButtonClick}
              onDeleteButtonClick={onDeleteButtonClick}
              onRowClick={onRowClick}
              onEditButtonClick={handleEditButtonClick}
              errorHandling={errorHandling}
            />
          );
        }
      }
    });

    if (array.length > 0) {
      return array;
    }
    return <div className="col-12 text-center p-3">Keine Wiedervorlagen vorhanden </div>;
  };

  const renderReminderList = () => {
    if (reminders.length > 0) {
      if (timeSeparation) {
        return (
          <React.Fragment>
            <h6 className="mb-3 mt-3">Letzte 24 Stunden</h6>
            <Paper>
              <List>{renderReminderItems(0, 1)}</List>
            </Paper>
            <h6 className="mb-3 mt-3">In Zukunft</h6>
            <Paper>
              <List>{renderReminderItems(-99999, 0)}</List>
            </Paper>
            <h6 className="mb-3 mt-3">Letzte Woche</h6>
            <Paper>
              <List>{renderReminderItems(1, 7)}</List>
            </Paper>
            <h6 className="mb-3 mt-3">Älter als 7 Tage</h6>
            <Paper>
              <List>{renderReminderItems(7, 999999)}</List>
            </Paper>
          </React.Fragment>
        );
      }
      return (
        <React.Fragment>
          <Paper>
            <List>{renderReminderItems(0, 0, false)}</List>
          </Paper>
        </React.Fragment>
      );
    }
    return <div className="col-12 text-center p-3">Keine Wiedervorlagen vorhanden </div>;
  };

  return (
    <React.Fragment>
      {renderReminderList()}
      {variant !== 'candidate' && updateCounterPartReminderPopUpIsOpen && (
        <UpdateCounterPartReminderPopUpContainer
          open={updateCounterPartReminderPopUpIsOpen}
          data={counterPartReminderToEdit}
          handleSnackbarOpen={handleSnackbarOpen}
          indexInformation="Wiedervorlage"
          handleClose={() => {
            onEditButtonClick();
            setUpdateCounterPartReminderPopUpIsOpen(false);
          }}
        />
      )}
    </React.Fragment>
  );
};

ReminderList.propTypes = {
  reminders: PropTypes.array.isRequired,
  onIsReadButtonClick: PropTypes.func.isRequired,
  onDeleteButtonClick: PropTypes.func.isRequired,
  onEditButtonClick: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  errorHandling: PropTypes.func.isRequired,
  timeSeparation: PropTypes.bool,
  variant: PropTypes.oneOf(['company', 'candidate']),
  handleSnackbarOpen: PropTypes.func.isRequired
};
ReminderList.defaultProps = {
  timeSeparation: false,
  variant: 'company'
};
export default ReminderList;
